import React, { useEffect, useRef, useState , useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import styles from './StrokeCheck.module.css';
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { getUserId } from '../utils/userId';

const StrokeCheck: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  // TODO(종현): any -> 타이핑 지정하기
  const [responseHistory, setResponseHistory] = React.useState<any[]>([]);

  const [isLoading, setIsLoading] = useState(true);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useLayoutEffect(() => {
      document.documentElement.style.overflow = 'auto';
      document.body.style.overflow = 'auto';
  
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
  
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
  
      const timer = setTimeout(() => {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }, 50);
  
      return () => clearTimeout(timer);
    }, []);

  const handleBack = () => {
    navigate(`${prefix}/check`);
  };

  const handleStartTest = () => {
    console.log('Navigating to /stroke/check/new');
    navigate(`${prefix}/stroke/check/new`);
  };

  const handleHistoryClick = (docId: string) => {
    navigate(`${prefix}/stroke/check/results/${docId}`);
  };

  useEffect(() => {
    const getStrokeCheckHistory = async () => {
      try {
        const userId = getUserId();
        if (!userId) {
          console.error('User ID is missing');
          setIsLoading(false); // 로딩 중지
          return;
        }

        const q = query(
          collection(db, 'userStrokeResponse'),
          where('userId', '==', userId)
        );

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          // 1. 히스토리가 없으면
          console.log('No stroke check history found. Redirecting to new test...');
          navigate(`${prefix}/stroke/check/new`, { replace: true }); // 즉시 새 테스트로 리다이렉트
        } else {
          // 2. 히스토리가 있으면
          const history = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          history.sort((a: any, b: any) => {
            const timeA = a.timestamp?.seconds || 0;
            const timeB = b.timestamp?.seconds || 0;
            return timeB - timeA;
          });
          setResponseHistory(history);
          setIsLoading(false); // 로딩 중지 (페이지 표시)
        }
      } catch (error) {
        console.error('Error fetching stroke check history:', error);
        setIsLoading(false); // 에러 시 로딩 중지
      }
    };
    const timer = setTimeout(() => {
        getStrokeCheckHistory();
      }, 300); // 0.3초 딜레이

      return () => clearTimeout(timer);
  }, [navigate]); 

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleScroll = () => {
      contentElement.classList.remove(styles.scrollHidden);
      
      clearTimeout(scrollTimerRef.current);

      scrollTimerRef.current = setTimeout(() => {
        contentElement.classList.add(styles.scrollHidden);
      }, 400); 
    };

    contentElement.addEventListener('scroll', handleScroll);
    contentElement.classList.add(styles.scrollHidden);

    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, [contentRef]); 

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <ArrowBack />
          </button>
          <span className={styles.headerText}>{t('check_before_1')}</span>
        </div>
        <div className={styles.content}>
          <div className={`${styles.contentScroll} ${styles.loadingContainer}`}>
            <div className={styles.spinner}></div>
            <div className={styles.loadingText}>
              {t('stroke_loading', '뇌졸중 검사 기록을 확인하고 있습니다...')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t('check_before_1')}</span>
      </div>
      <div className={styles.content}>
        <div className={styles.contentScroll} ref={contentRef}>
        {responseHistory.length > 0 ? (
          <div className={styles.historyList}>
            {responseHistory.map((response, index) => {
              const currentLanguage = i18n.language;
              const timestamp =
                typeof response.timestamp?.seconds === 'number'
                  ? new Date(response.timestamp.seconds * 1000)
                  : new Date(); // fallback
              const formattedDate = timestamp.toLocaleDateString(currentLanguage, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              });

              const score = response.totalRiskCount || 0;
              const total = response.totalQuestions || 23; 
              const percentage = total === 0 ? 0 : (score / total) * 100;
              
              let scoreClass = styles.scoreGreen; 
              if (percentage >= 60) { 
                scoreClass = styles.scoreRed;
              } else if (percentage >= 30) { 
                scoreClass = styles.scoreOrange;
              }

              return (
                <div key={response.id || index} className={styles.historyItem} onClick={() => handleHistoryClick(response.id)} style={{ cursor: 'pointer'}}>
                  <div className={styles.historyLeft}>
                    <div className={styles.historyDate}>{formattedDate}</div>
                  </div>
                  <div className={styles.historyScore}>
                    <div className={`${styles.scoreCircle} ${scoreClass}`}>
                      {score}
                    </div>
                    <span className={styles.scoreTotalText}>
                      / {total}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.noHistory}>No test results found.</div>
        )}
      </div>
      </div>
      <button className={styles.startTestButton} onClick={handleStartTest}>
        {t('start_test_button')}
      </button>
    </div>
  );
};

export default StrokeCheck; 